import { useCallback, useEffect, useRef, useState } from 'react';
import { getFirestore, doc, updateDoc, runTransaction, arrayUnion } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

// PDF document management for a property doc: upload to Firebase Storage,
// append metadata to `data.documents`, transactional delete that also strips
// the Storage object. Encapsulates ~130 lines of inline logic from
// LeadBuyerMatches.js.

const MAX_BYTES = 10 * 1024 * 1024; // 10MB

function generateDocId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // Fallback uuid (mirrors prior inline behaviour) for older runtimes
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (
        // eslint-disable-next-line no-bitwise
        c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16));
}

export function usePropertyDocuments({ companyId, propertyId, initial }) {
    const [documents, setDocuments] = useState(Array.isArray(initial) ? initial : []);
    const [progress, setProgress] = useState(null); // 0..1 | null
    const [error, setError] = useState(null);
    const uploadingRef = useRef(false);
    const mountedRef = useRef(true);

    // Gate post-await setState so an upload or delete that resolves after the
    // user navigates away doesn't write to an unmounted component. The Storage
    // upload itself still completes in the background — only state updates are
    // suppressed.
    useEffect(() => () => { mountedRef.current = false; }, []);

    // Resync documents when parent reloads with a new initial list (e.g. lead nav).
    // Caller controls cadence by passing a stable `initial` ref.
    const reset = useCallback((next) => {
        setDocuments(Array.isArray(next) ? next : []);
        setError(null);
    }, []);

    const upload = useCallback(async (file) => {
        if (!file || !companyId || !propertyId) return;
        if (uploadingRef.current) return;

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            if (mountedRef.current) setError('Only PDF files are supported.');
            return;
        }
        if (file.size > MAX_BYTES) {
            if (mountedRef.current) setError('File must be under 10MB.');
            return;
        }

        uploadingRef.current = true;
        if (mountedRef.current) {
            setError(null);
            setProgress(0);
        }

        try {
            const docId = generateDocId();
            const storagePath = `documents/${companyId}/${propertyId}/${docId}.pdf`;
            const storage = getStorage();
            const storageRef = ref(storage, storagePath);
            const task = uploadBytesResumable(storageRef, file, { contentType: 'application/pdf' });

            const downloadURL = await new Promise((resolve, reject) => {
                task.on('state_changed',
                    (snapshot) => {
                        if (mountedRef.current) setProgress(snapshot.bytesTransferred / snapshot.totalBytes);
                    },
                    reject,
                    async () => {
                        try { resolve(await getDownloadURL(task.snapshot.ref)); }
                        catch (e) { reject(e); }
                    },
                );
            });

            const meta = {
                id: docId,
                name: file.name,
                url: downloadURL,
                size: file.size,
                storagePath,
                uploadedAt: new Date(),
            };

            const db = getFirestore();
            const propRef = doc(db, 'companies', companyId, 'properties', propertyId);
            await updateDoc(propRef, { 'data.documents': arrayUnion(meta) });

            if (mountedRef.current) setDocuments((prev) => [...prev, meta]);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Document upload failed:', err);
            if (mountedRef.current) setError('Upload failed. Please try again.');
        } finally {
            if (mountedRef.current) setProgress(null);
            uploadingRef.current = false;
        }
    }, [companyId, propertyId]);

    const remove = useCallback(async (item) => {
        if (!companyId || !propertyId || !item) return;
        try {
            if (item.storagePath) {
                try {
                    const storage = getStorage();
                    await deleteObject(ref(storage, item.storagePath));
                } catch (err) {
                    // File may already be gone — continue with Firestore cleanup.
                    // eslint-disable-next-line no-console
                    console.warn('Storage delete failed (may already be deleted):', err.message);
                }
            }
            const db = getFirestore();
            const propRef = doc(db, 'companies', companyId, 'properties', propertyId);
            await runTransaction(db, async (tx) => {
                const snap = await tx.get(propRef);
                if (!snap.exists()) return;
                const current = snap.data()?.data?.documents || [];
                const updated = current.filter((d) => d.id !== item.id);
                tx.update(propRef, { 'data.documents': updated });
            });
            if (mountedRef.current) setDocuments((prev) => prev.filter((d) => d.id !== item.id));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Document delete failed:', err);
        }
    }, [companyId, propertyId]);

    return { documents, upload, remove, progress, error, reset };
}
